import { inject } from '@angular/core';
import { type CanActivateFn, Router } from '@angular/router';
import { TenantUserRoles } from '@buspro/shared/common';
import { TenantUsersApi } from '@buspro/web/apis';
import { NgxAuthService } from '@corelabs/ng-auth';
import { type Observable, filter, firstValueFrom, lastValueFrom, map, race, timer } from 'rxjs';

type SignedInUser = { id?: string; role?: string } | null;

const TENANT_ROLES: string[] = Object.values(TenantUserRoles);

/** Waits for the session on a hard reload, and gives up rather than hold the page: the server still decides. */
async function signedInUser(): Promise<SignedInUser> {
  const auth = inject(NgxAuthService);
  const user$ = (auth.state as unknown as Observable<SignedInUser>).pipe(filter((user) => !!user));
  return firstValueFrom(race(user$, timer(4000).pipe(map(() => null as SignedInUser))));
}

/**
 * Someone following a link to a page that isn't theirs gets a calm page, not a screen of failed
 * requests. Only tidies the screens: the server is what refuses. BusPro's own staff use these same
 * modules in the admin portal, so a role outside the tenant's is never turned away here.
 */
export function requireRoles(...roles: TenantUserRoles[]): CanActivateFn {
  return async () => {
    const router = inject(Router);
    const user = await signedInUser();
    if (!user?.role || !TENANT_ROLES.includes(user.role)) return true;
    return (roles as string[]).includes(user.role) || router.createUrlTree(['/no-access']);
  };
}

/** The same for a parcel clerk's duties, which a role alone cannot tell. */
export function requireParcelDuty(duty: 'collections' | 'pickup'): CanActivateFn {
  return async () => {
    const router = inject(Router);
    const api = inject(TenantUsersApi);
    const user = await signedInUser();
    if (user?.role !== TenantUserRoles.TERMINAL_PARCEL_CLERK || !user.id) return true;
    try {
      const duties = await lastValueFrom(api.tenantUsersControllerGetParcelDuties({ id: user.id }));
      return duties[duty] || router.createUrlTree(['/no-access']);
    } catch {
      return true;
    }
  };
}
